<template>
  <div class="page-content-wrapper">
    <div class="row justify-content-center">
      <div class="col-sm-12">
        <div class="card border-1 rounded-3 shadow-sm">
          <div class="card-header bg-white border-bottom py-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="fs-5 mb-0 fw-bold text-dark">Behalf Application</h5>
                <small class="text-muted text-xs"
                  >Process workflow services in order.</small
                >
              </div>
              <div v-if="services.length" class="text-end">
                <small class="text-muted fw-medium"
                  >Step {{ currentStepNumber }} of {{ services.length }}</small
                >
              </div>
            </div>
          </div>
          <div class="card-body p-3">
            <!-- Application Check Loading -->
            <div v-if="!applicationCheckComplete" class="text-center py-4">
              <div
                class="spinner-border text-primary"
                role="status"
                style="width: 2rem; height: 2rem"
              >
                <span class="visually-hidden">Checking application...</span>
              </div>
              <p class="mt-2 mb-0 text-muted small">Verifying application…</p>
            </div>

            <!-- No Application Found -->
            <div v-else-if="!applicationExists" class="py-3">
              <MessageCard
                icon="fas fa-exclamation-triangle"
                title="No Application Found"
                :message="
                  errorMessage ||
                  'No active application found for this workflow. Click below to start a new application.'
                "
              />
              <div class="text-center mt-3 d-flex justify-content-center gap-2">
                <button
                  class="btn btn-primary rounded-3 text-xs btn-sm"
                  @click="createNewApplication"
                >
                  <i class="fas fa-plus me-2"></i>Start New Application
                </button>
                <button
                  class="btn btn-bg-green rounded-3 text-xs btn-sm"
                  @click="$router.push({ name: backRouteName })"
                >
                  <i class="fas fa-arrow-left me-2"></i>{{ backButtonText }}
                </button>
              </div>
            </div>

            <!-- Services Loading -->
            <div v-else-if="servicesLoading" class="text-center py-4">
              <div
                class="spinner-border text-primary"
                role="status"
                style="width: 2rem; height: 2rem"
              >
                <span class="visually-hidden"
                  >Loading workflow services...</span
                >
              </div>
              <p class="mt-2 mb-0 text-muted small">Loading services…</p>
            </div>

            <!-- Services Content -->
            <div v-else>
              <div v-if="currentComponent" class="service-stage">
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <div>
                    <h6 class="mb-1 text-dark fs-6 fw-semibold">
                      {{ currentServiceName }}
                    </h6>
                    <small class="text-muted text-xs"
                      >Complete the following service to complete the
                      application.</small
                    >
                  </div>
                  <span class="badge bg-light text-primary px-2 py-1 small">
                    In Progress
                  </span>
                </div>
                <hr class="text-secondary" />
                <component
                  :is="currentComponent"
                  @service-completed="onServiceCompleted"
                ></component>
              </div>

              <div v-else-if="loading" class="text-center py-4">
                <div
                  class="spinner-border text-primary"
                  role="status"
                  style="width: 1.5rem; height: 1.5rem"
                >
                  <span class="visually-hidden">Loading service...</span>
                </div>
                <p class="mt-2 mb-0 text-muted small">Preparing service…</p>
              </div>

              <div v-else-if="errorMessage" class="py-3">
                <MessageCard
                  icon="fas fa-exclamation-circle"
                  title="Issue encountered"
                  :message="errorMessage"
                />
                <div class="text-center mt-3">
                  <button
                    class="btn btn-outline-danger btn-sm px-3 py-2 rounded-3"
                    @click="retryCurrentService"
                  >
                    <i class="fas fa-redo me-2"></i>Retry
                  </button>
                </div>
              </div>

              <div v-else class="text-center py-4">
                <i
                  class="bi bi-sliders2-vertical text-muted mb-2"
                  style="font-size: 1.5rem"
                ></i>
                <h6 class="text-muted mb-1">No Services</h6>
                <small class="text-muted">No services available yet.</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import { validateToken } from "@/utils/auth";
import MessageCard from "@/layouts/client/customer_data/employee_screening/components/MessageCard.vue";
import Swal from "sweetalert2";

// Import all credit bureau service components
import AddressMatchVerification from "@/layouts/credit_bureau_services/address_match_verification/AddressMatchVerification.vue";
import AfisCrimCheck from "@/layouts/credit_bureau_services/afis_crim_check/AfisCrimCheck.vue";
import AMLVerification from "@/layouts/credit_bureau_services/aml_verification/AMLVerification.vue";
import BankAccountIndividualVerification from "@/layouts/credit_bureau_services/bank_account_individual_verification/BankAccountIndividualVerification.vue";
import BankAccountVerification from "@/layouts/credit_bureau_services/bank_account_verification/BankAccountVerification.vue";
import CIPCVerification from "@/layouts/credit_bureau_services/cipc_verification/CIPCVerification.vue";
import CreditReport from "@/layouts/credit_bureau_services/credit_report/CreditReport.vue";
import CustomService from "@/layouts/credit_bureau_services/custom_service/CustomService.vue";
import EligibilityService from "@/layouts/credit_bureau_services/eligibility_service/EligibilityService.vue";
import EnterpriseAMLVerification from "@/layouts/credit_bureau_services/enterprise_aml_verification/EnterpriseAMLVerification.vue";
import EnterpriseTransunionCreditVerification from "@/layouts/credit_bureau_services/enterprise_transunion_credit_verification/EnterpriseTransUnionCreditVerification.vue";
import EnterpriseTransunionKYBVerification from "@/layouts/credit_bureau_services/enterprise_transunion_kyb_verification/EnterpriseTransUnionKYBVerification.vue";
import ExperianCreditReport from "@/layouts/credit_bureau_services/experian_credit_report/ExperianCreditReport.vue";
import IncomeEstimator from "@/layouts/credit_bureau_services/income_estimator/IncomeEstimator.vue";
import IncomePredictor from "@/layouts/credit_bureau_services/income_predictor/IncomePredictor.vue";
import MastercardMatchInquiry from "@/layouts/credit_bureau_services/mastercard_match_inquiry/MastercardMatchInquiry.vue";
import OCRVerification from "@/layouts/credit_bureau_services/ocr_verification/OcrVerification.vue";
import QualCheck from "@/layouts/credit_bureau_services/qual_check/QualCheck.vue";
import RefugeeVerificationService from "@/layouts/credit_bureau_services/refugee_verification_service/RefugeeVerificationService.vue";
import RSAIDVerification from "@/layouts/credit_bureau_services/rsa_id_verification/RsaIdVerification.vue";
import SearchFcra from "@/layouts/credit_bureau_services/search_fcra/SearchFcra.vue";
import TrustDataFetch from "@/layouts/credit_bureau_services/trust_data_fetch/TrustDataFetch.vue";
import AcademicPost92CheckVerification from "@/layouts/credit_bureau_services/academic_post_92_check_verification/AcademicPost92CheckVerification.vue";
import HuruCriminalCheckVerification from "@/layouts/credit_bureau_services/huru_criminal_check_verification/HuruCriminalCheckVerification.vue";
import CompanyVerificationAndScore from "@/layouts/credit_bureau_services/company_verification_and_score/CompanyVerificationAndScore.vue";
import EmployeeScreeningService from "@/layouts/credit_bureau_services/employee_screening_service/EmployeeScreeningService.vue";
import CreditReportNamibia from "@/layouts/credit_bureau_services/credit_report_namibia/CreditReportNamibia.vue";
import TelephoneList from "@/layouts/credit_bureau_services/telephones/Telephones.vue";

// Import IDV/Liveness verification components
import RsaIdLivenessVerification from "@/layouts/credit_bureau_services/rsa_id_liveness_verification/RsaIdLivenessVerification.vue";
import RefugeeLivenessVerification from "@/layouts/credit_bureau_services/refugee_liveness_verification/RefugeeLivenessVerification.vue";
import FacialLivenessCheckVerification from "@/layouts/credit_bureau_services/facial_liveness_verification/FacialLivenessCheckVerification.vue";
import FacialBiometricsVerification from "@/layouts/credit_bureau_services/facial_biometrics_verification/FacialBiometricsVerification.vue";
import ComprehensiveLivenessVerification from "@/layouts/credit_bureau_services/comprehensive_liveness_verification/ComprehensiveLivenessVerification.vue";

// Map service_name to component name
const SERVICE_COMPONENT_MAP = {
  "Address Match": "AddressMatchVerification",
  "AFIS Criminal Check": "AfisCrimCheck",
  "Anti Money Laundering": "AMLVerification",
  "Bank Account Individual Verification": "BankAccountIndividualVerification",
  "Bank Account Company Verification": "BankAccountVerification",
  "CIPC Verification": "CIPCVerification",
  "Credit Report": "CreditReport",
  "Custom Service": "CustomService",
  "Eligibility Solution": "EligibilityService",
  "Enterprise Anti Money Laundering": "EnterpriseAMLVerification",
  "Enterprise Credit Check": "EnterpriseTransunionCreditVerification",
  "Enterprise KYB": "EnterpriseTransunionKYBVerification",
  "Experian Credit Report (PDF)": "ExperianCreditReport",
  "Income Estimator": "IncomeEstimator",
  "Income Predictor": "IncomePredictor",
  "Mastercard Match": "MastercardMatchInquiry",
  "OCR verification": "OCRVerification",
  "Qualification Check": "QualCheck",
  "Refugee Verification": "RefugeeVerificationService",
  "RSA ID Verification": "RSAIDVerification",
  "Search FCRA": "SearchFcra",
  "Trust Data": "TrustDataFetch",
  "Academic Post 92 Check": "AcademicPost92CheckVerification",
  "South African Criminal Check": "HuruCriminalCheckVerification",
  "CIPC Plus Score": "CompanyVerificationAndScore",
  "Entity Data Capture": "CustomService",
  "Employee Screening Service": "EmployeeScreeningService",
  "Credit Report Namibia": "CreditReportNamibia",
  "Telephones": "TelephoneList",
  // IDV/Liveness verification services
  "RSA ID Liveness Verification": "RsaIdLivenessVerification",
  "Refugee Liveness Verification": "RefugeeLivenessVerification",
  "ID Verification (Liveness check)": "FacialLivenessCheckVerification",
  "ID Verification (Facial Biometrics)": "FacialBiometricsVerification",
  "Facial Biometrics Verification": "FacialBiometricsVerification",
  "Comprehensive Liveness Verification": "ComprehensiveLivenessVerification",
};

const FLEX_NAME_COMPONENT_MAP = {
  address_match_verification: "AddressMatchVerification",
  afis_criminal_check_verification: "AfisCrimCheck",
  aml_verification: "AMLVerification",
  bank_account_individual_verification: "BankAccountIndividualVerification",
  bank_account_verification: "BankAccountVerification",
  cipc_verification: "CIPCVerification",
  id_credit_report: "CreditReport",
  eligibility_service_verification: "EligibilityService",
  enterprise_aml_verification: "EnterpriseAMLVerification",
  enterprise_transunion_credit_verification:
    "EnterpriseTransunionCreditVerification",
  enterprise_transunion_kyb_verification: "EnterpriseTransunionKYBVerification",
  experian_credit_report_verification: "ExperianCreditReport",
  income_estimation_verification: "IncomeEstimator",
  income_predictor_verification: "IncomePredictor",
  mastercard_match_inquiry: "MastercardMatchInquiry",
  ocr_verification: "OCRVerification",
  qualification_check_verification: "QualCheck",
  refugee_verification: "RefugeeVerificationService",
  rsa_id_verification: "RSAIDVerification",
  search_fcra_verification: "SearchFcra",
  trust_name_search_verification: "TrustDataFetch",
  academic_post_92_check_verification: "AcademicPost92CheckVerification",
  huru_criminal_check_verification: "HuruCriminalCheckVerification",
  company_verification_and_score: "CompanyVerificationAndScore",
  entity_data_capture: "CustomService",
  employee_screening_service: "EmployeeScreeningService",
  credit_report_namibia: "CreditReportNamibia",
  telephone_list: "Telephones",
  // IDV/Liveness verification flex names
  rsa_id_liveness_verification: "RsaIdLivenessVerification",
  refugee_liveness_verification: "RefugeeLivenessVerification",
  facial_liveness_check_verification: "FacialLivenessCheckVerification",
  facial_biometrics_verification: "FacialBiometricsVerification",
  comprehensive_liveness_verification: "ComprehensiveLivenessVerification",
};

export default {
  name: "BehalfApplication",
  props: {
    workflowId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      services: [],
      currentServiceIndex: 0,
      currentComponent: null,
      loading: false,
      errorMessage: null,
      completedServices: [],
      servicesLoading: false,
      applicationExists: false,
      applicationCheckComplete: false,
      nextServiceData: null,
      customerId: null,
      applicationId: null,
    };
  },
  computed: {
    currentServiceName() {
      return this.services[this.currentServiceIndex]?.name || "Unknown Service";
    },
    currentStepNumber() {
      if (this.services.length === 0) {
        return 0;
      }
      return Math.min(this.services.length, this.completedServices.length + 1);
    },
    isToolboxClient() {
      const domain = window.location.hostname;
      const role = this.$store.getters.get_user_role;
      const targetDomains = ['swift', 'mobi', 'suzuki', 'mobiafrica'];
      return role === 'Client' && targetDomains.some(d => domain.includes(d));
    },
    backButtonText() {
      return this.isToolboxClient ? 'Back to Toolbox' : 'Back to Projects';
    },
    backRouteName() {
      return this.isToolboxClient ? 'toolbox' : 'project_dashboard';
    },
  },
  components: {
    AddressMatchVerification,
    AfisCrimCheck,
    AMLVerification,
    BankAccountIndividualVerification,
    BankAccountVerification,
    CIPCVerification,
    CreditReport,
    CustomService,
    EligibilityService,
    EnterpriseAMLVerification,
    EnterpriseTransunionCreditVerification,
    EnterpriseTransunionKYBVerification,
    ExperianCreditReport,
    IncomeEstimator,
    IncomePredictor,
    MastercardMatchInquiry,
    OCRVerification,
    QualCheck,
    RefugeeVerificationService,
    RSAIDVerification,
    SearchFcra,
    TrustDataFetch,
    MessageCard,
    AcademicPost92CheckVerification,
    HuruCriminalCheckVerification,
    CompanyVerificationAndScore,
    EmployeeScreeningService,
    CreditReportNamibia,
    TelephoneList,
    // IDV/Liveness verification components
    RsaIdLivenessVerification,
    RefugeeLivenessVerification,
    FacialLivenessCheckVerification,
    FacialBiometricsVerification,
    ComprehensiveLivenessVerification,
  },
  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }

    this.$store.dispatch("update_workflow_id", this.workflowId);
    this.checkApplicationExists();
  },
  methods: {
    async createNewApplication() {
      const TOKEN = localStorage.getItem("token");
      const API_URL = `${API_BASE_URL}/verify_api/workflow_management_api/create_behalf_application_api/`;

      // Show loading
      Swal.fire({
        title: 'Creating Application...',
        text: 'Please wait while we set up your application.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${TOKEN}`,
          },
          body: JSON.stringify({
            workflow_id: this.workflowId,
          }),
        });

        const data = await response.json();
        Swal.close();

        if (response.ok && data.status === "success") {
          // Reload to start the application
          window.location.reload();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: data.message || 'Failed to create application. Please try again.',
          });
        }
      } catch (error) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while creating the application.',
        });
      }
    },
    async checkApplicationExists() {
      this.applicationCheckComplete = false;
      const token = localStorage.getItem("token");
      const endpoint = `${API_BASE_URL}/verify_api/workflow_management_api/check_behalf_application_api/${this.workflowId}/`;

      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        });

        const payload = await response.json();

        if (response.ok && payload.status === "success") {
          this.applicationExists = true;

          this.customerId = this.$store.getters.get_user_id;
          if (payload.data) {
            this.applicationId = payload.data.application_id || null;
          }

          // Check if next_service is null but completed is false
          // This means all services are done but workflow wasn't marked complete
          if (
            payload.data &&
            payload.data.next_service === null &&
            payload.data.completed === false
          ) {
            // Mark workflow as complete and show completion dialog
            await this.markWorkflowComplete();
            return;
          }

          // Store next_service data if available
          if (payload.data && payload.data.next_service) {
            this.nextServiceData = payload.data.next_service;
          }
          this.fetchWorkflowServices();
        } else {
          this.applicationExists = false;
          this.errorMessage =
            payload.message ||
            "No active application found for this workflow. Please create an application first.";
        }
      } catch (error) {
        this.applicationExists = false;
        this.errorMessage =
          "An error occurred while checking your application status.";
      } finally {
        this.applicationCheckComplete = true;
      }
    },
    async fetchWorkflowServices() {
      this.servicesLoading = true;
      this.errorMessage = null;
      const token = localStorage.getItem("token");
      const endpoint = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_services/${this.workflowId}`;

      try {
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to load workflow services.");
        }

        const payload = await response.json();

        if (payload.status !== "success" || !Array.isArray(payload.data)) {
          throw new Error(
            payload.message || "Workflow services could not be retrieved."
          );
        }

        const mappedServices = payload.data
          .map((item, index) => {
            const serviceData = item.service || item.form || {};
            const serviceName =
              serviceData.service_name || serviceData.form_name;
            let componentKey;
            if (serviceData.flex_name === null) {
              componentKey = "CustomService";
            } else {
              componentKey = SERVICE_COMPONENT_MAP[serviceName];
              // If not found, componentKey remains undefined, triggering error in ensureCurrentServiceIsRenderable
            }

            return {
              id: serviceData.id,
              workflowProjectServiceId: item.workflow_project_service_id,
              name: serviceName || `Service ${index + 1}`,
              description: this.resolveServiceDescription(serviceData),
              order: item.service_order ?? item.order ?? null,
              componentKey,
              raw: item,
            };
          })
          .filter((service) => !!service.name);

        mappedServices.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;

          if (orderA === orderB) {
            return (a.raw?.position ?? 0) - (b.raw?.position ?? 0);
          }
          return orderA - orderB;
        });

        this.services = mappedServices;
        this.currentServiceIndex = 0;
        this.completedServices = [];

        if (this.services.length === 0) {
          this.currentComponent = null;
          return;
        }

        // If we have next_service data from the application check,
        // find and set the correct service index
        if (this.nextServiceData) {
          await this.setCurrentServiceFromNextService();
        }

        this.ensureCurrentServiceIsRenderable();
      } catch (error) {
        this.errorMessage =
          error.message ||
          "An unexpected error occurred while loading services.";
        this.services = [];
        this.currentComponent = null;
      } finally {
        this.servicesLoading = false;
      }
    },
    async setCurrentServiceFromNextService() {
      if (!this.nextServiceData) {
        return;
      }

      const nextService = this.nextServiceData;

      // Find the service in the services array that matches the next_service
      const serviceIndex = this.services.findIndex((service) => {
        // Match by service_id if available
        if (nextService.service_id && service.id === nextService.service_id) {
          return true;
        }
        // Fallback: Match by service_name
        if (
          nextService.service_name &&
          service.name === nextService.service_name
        ) {
          return true;
        }
        return false;
      });

      if (serviceIndex !== -1) {
        // Found the next service - set it as current
        this.currentServiceIndex = serviceIndex;

        // Determine component based on service type
        let componentKey = null;

        if (nextService.service_type === "system generated") {
          // System generated services always use CustomService
          componentKey = "CustomService";
        } else {
          // For other service types, use SERVICE_COMPONENT_MAP
          componentKey = SERVICE_COMPONENT_MAP[nextService.service_name];

          if (!componentKey) {
            // Service not found in mapping
            this.errorMessage = `The service "${nextService.service_name}" is not yet supported in this flow.`;
            this.currentComponent = null;
            return;
          }
        }

        // Set service_id in store for CustomService to use
        if (componentKey === "CustomService" && nextService.service_id) {
          this.$store.commit("set_service_id", nextService.service_id);
        }

        const serviceName = this.services[serviceIndex]?.name;
        if (serviceName === 'Entity Data Capture' && nextService.service_id) {
          await this.fetchEntityDataCaptureConfig(nextService.service_id);
        }

        // Update the service's componentKey
        this.services[serviceIndex].componentKey = componentKey;

        // Mark all previous services as completed
        this.completedServices = [];
        for (let i = 0; i < serviceIndex; i++) {
          this.completedServices.push(i);
        }

        // Load the service
        this.ensureCurrentServiceIsRenderable();
      } else {
        this.errorMessage =
          "Unable to find the next service in the workflow configuration.";
      }
    },
    ensureCurrentServiceIsRenderable() {
      const service = this.services[this.currentServiceIndex];

      if (!service) {
        this.currentComponent = null;
        this.errorMessage = "No service is available to display.";
        return;
      }

      if (!service.componentKey) {
        this.currentComponent = null;
        this.errorMessage = `The service "${service.name}" is not yet supported in this flow. Please contact support if this issue persists.`;
        return;
      }

      this.loadCurrentService();
    },
    loadCurrentService() {
      this.loading = true;
      this.errorMessage = null;

      const service = this.services[this.currentServiceIndex];
      if (service) {
        this.currentComponent = service.componentKey;
      } else {
        this.currentComponent = null;
        this.errorMessage = "No more services available";
      }

      this.loading = false;
    },

    async onServiceCompleted(data) {
      // Mark current service as completed
      this.completedServices.push(this.currentServiceIndex);

      // Use the provided data from main controller to set the next service
      await this.setNextServiceFromData(data);
    },

    async setNextServiceFromData(nextServiceData) {
      
      // Handle workflow completion
      if (nextServiceData.status === "Workflow completed") {
        this.showStartNewApplicationDialog();
        return;
      }

      // Determine the component to use based on main controller response
      let componentKey = null;

      if (nextServiceData.flex_name === null) {
        // When flex_name is null, use CustomService
        componentKey = "CustomService";
      } else {
        // Use FLEX_NAME_COMPONENT_MAP to get the component
        componentKey = FLEX_NAME_COMPONENT_MAP[nextServiceData.flex_name];

        if (!componentKey) {
          // Service not found in mapping
          this.errorMessage = `The service "${nextServiceData.service_name}" is not yet supported in this flow.`;
          this.currentComponent = null;
          return;
        }
      }

      // Set service_id in store for CustomService to use
      if (componentKey === "CustomService" && nextServiceData.service_id) {
        this.$store.commit("set_service_id", nextServiceData.service_id);
      }

      // Find the service in the services array
      const serviceIndex = this.services.findIndex((service) => {
        // Match by service_id first (most reliable)
        if (
          nextServiceData.service_id &&
          service.id === nextServiceData.service_id
        ) {
          return true;
        }
        // Fallback: Match by service_name
        if (service.name === nextServiceData.service_name) {
          return true;
        }
        return false;
      });

      if (serviceIndex !== -1) {
        // Found the next service - set it as current
        this.currentServiceIndex = serviceIndex;

        // Update the service's componentKey
        this.services[serviceIndex].componentKey = componentKey;

        // Mark all previous services as completed
        this.completedServices = [];
        for (let i = 0; i < serviceIndex; i++) {
          this.completedServices.push(i);
        }

        // Load the next service
        this.ensureCurrentServiceIsRenderable();
      } else {
        // If not found, try to move to next sequential service as fallback
        this.currentServiceIndex++;
        if (this.currentServiceIndex < this.services.length) {
          // Update the next sequential service with the component key
          this.services[this.currentServiceIndex].componentKey = componentKey;
          this.ensureCurrentServiceIsRenderable();
        } else {
          // Show completion dialog only after successful linking (if applicable)
          this.showStartNewApplicationDialog();
        }
      }
    },

    onServiceError(error) {
      this.loading = false;
      this.errorMessage = error || "Service encountered an error";
    },

    retryCurrentService() {
      this.errorMessage = null;
      if (this.services.length === 0) {
        this.fetchWorkflowServices();
        return;
      }

      this.ensureCurrentServiceIsRenderable();
    },
    
    async fetchEntityDataCaptureConfig(serviceId) {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/verify_api/service_management_api/get_entity_data_capture_config/?workflow_id=${this.workflowId}&service_id=${serviceId}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
        });

        const data = await response.json();

        if (response.ok && data.status === 'success' && data.data) {
          this.$store.commit('set_entity_data_capture_config', data.data);
        } else {
          this.$store.commit('clear_entity_data_capture_config');
        }
      } catch (error) {
        this.$store.commit('clear_entity_data_capture_config');
      }
    },

    async showStartNewApplicationDialog() {
      Swal.fire({
        title: "Workflow Complete!",
        text: "You have successfully completed this workflow. Would you like to start another application?",
        icon: "success",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Start New Application",
        denyButtonText: "View Application",
        cancelButtonText: this.isToolboxClient ? "Back to Toolbox" : "Back to Dashboard",
        confirmButtonColor: "#28a745",
        denyButtonColor: "#0d6efd",
        cancelButtonColor: "#6c757d",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.startNewApplicationDialog(this.workflowId);
        } else if (result.isDenied) {
          this.viewApplication();
        } else {
          this.$router.push({ name: this.isToolboxClient ? "workbench" : "project_dashboard" });
        }
      });
    },

    viewApplication() {
      if (this.customerId && this.applicationId) {
        this.$router.push({
          name: 'customer_data',
          params: { id: this.customerId, application: this.applicationId }
        });
      }
    },

    startNewApplicationDialog(workflow_id) {
      const TOKEN = localStorage.getItem("token");
      const API_URL = `${API_BASE_URL}/verify_api/workflow_management_api/create_behalf_application_api/`;

      // Show loading state
      this.loading = true;

      $.ajax({
        url: API_URL,
        method: "POST",
        contentType: "application/json",
        data: JSON.stringify({
          workflow_id: workflow_id,
        }),
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          this.loading = false;
          if (response.status === "success") {
            window.location.reload();
          } else {
            this.showError(
              response.message || "Failed to create behalf application"
            );
          }
        },
        error: (error) => {
          this.loading = false;
          const errorMessage =
            error.responseJSON?.message ||
            "An error occurred while creating the behalf application";
          this.showError(errorMessage);
        },
      });
    },

    resolveComponentKey(serviceName) {
      if (!serviceName) {
        return null;
      }
      return SERVICE_COMPONENT_MAP[serviceName] || null;
    },

    resolveServiceDescription(serviceData) {
      if (!serviceData) {
        return "Description unavailable for this service.";
      }

      if (
        Array.isArray(serviceData.service_details) &&
        serviceData.service_details.length > 0
      ) {
        return (
          serviceData.service_details[0].service_description ||
          "Description unavailable for this service."
        );
      }

      if (serviceData.service_description) {
        return serviceData.service_description;
      }

      if (serviceData.form_description) {
        return serviceData.form_description;
      }

      return "Description unavailable for this service.";
    },
  },
};
</script>

<style scoped>

.card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
}

.card-title {
  font-size: 1.125rem;
}

.service-stage {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.service-stage h6 {
  font-size: 1rem;
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
}

.alert {
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

@media (max-width: 991.98px) {
  .behalf-application {
    padding: 0.5rem 0;
  }

  .service-stage {
    padding: 12px;
  }
}

.fw-semibold {
  font-weight: 600;
}
</style>
